(function () {
  const instances = new Map();
  const desktopMedia = window.matchMedia('(min-width: 990px)');
  const reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  class ScrollableTabsSection {
    constructor(section) {
      this.section = section;
      this.sectionRoot =
        section.matches && section.matches('[data-scrollable-tabs-section]')
          ? section
          : section.querySelector('[data-scrollable-tabs-section]');
      this.root = section.querySelector('[data-scrollable-tabs-root]');
      this.stickyFrame = section.querySelector('[data-scrollable-tabs-sticky-frame]');
      this.stickyHeader = section.querySelector('[data-scrollable-tabs-sticky-header]');
      this.body = section.querySelector('[data-scrollable-tabs-body]');
      this.triggerBar = section.querySelector('[data-tablist]');
      this.triggers = Array.from(section.querySelectorAll('[data-tab-trigger]'));
      this.panels = Array.from(section.querySelectorAll('[data-tab-panel]'));
      this.cards = Array.from(section.querySelectorAll('[data-tab-card]'));
      this.steps = Array.from(section.querySelectorAll('[data-tab-step]'));
      this.panelContainer = section.querySelector('[data-tab-panel-container]');
      this.currentIndex = 0;
      this.ticking = false;
      this.scrollListenersAttached = false;

      if (!this.root || !this.triggers.length || !this.panels.length || !this.cards.length) {
        return;
      }

      this.handleTriggerClick = this.handleTriggerClick.bind(this);
      this.handleTriggerKeydown = this.handleTriggerKeydown.bind(this);
      this.handleCardClick = this.handleCardClick.bind(this);
      this.handleCardKeydown = this.handleCardKeydown.bind(this);
      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.refreshLayoutMetrics = this.refreshLayoutMetrics.bind(this);
      this.scheduleScrollUpdate = this.scheduleScrollUpdate.bind(this);

      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', this.handleTriggerClick);
        trigger.addEventListener('keydown', this.handleTriggerKeydown);
      });

      this.cards.forEach((card) => {
        card.addEventListener('click', this.handleCardClick);
        card.addEventListener('keydown', this.handleCardKeydown);
      });

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => {
          this.refreshLayoutMetrics();
          this.scheduleScrollUpdate();
        });
        this.panels.forEach((panel) => this.resizeObserver.observe(panel));
        this.cards.forEach((card) => this.resizeObserver.observe(card));
        [this.stickyFrame, this.stickyHeader, this.body, this.root].forEach((element) => {
          if (element) {
            this.resizeObserver.observe(element);
          }
        });
        if (this.triggerBar) {
          this.resizeObserver.observe(this.triggerBar);
        }
      } else {
        window.addEventListener('resize', this.refreshLayoutMetrics);
      }

      this.bindMediaListener(desktopMedia, this.handleMediaChange);
      this.bindMediaListener(reducedMotionMedia, this.handleMediaChange);

      this.handleMediaChange();
      this.setActive(0, { skipCardUpdate: true });

      requestAnimationFrame(() => {
        this.refreshLayoutMetrics();
        this.scheduleScrollUpdate();
      });
    }

    bindMediaListener(mediaQuery, handler) {
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handler);
      } else {
        mediaQuery.addListener(handler);
      }
    }

    unbindMediaListener(mediaQuery, handler) {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handler);
      } else {
        mediaQuery.removeListener(handler);
      }
    }

    attachScrollListeners() {
      if (this.scrollListenersAttached) {
        return;
      }

      window.addEventListener('scroll', this.scheduleScrollUpdate, { passive: true });
      window.addEventListener('resize', this.scheduleScrollUpdate);
      window.addEventListener('load', this.scheduleScrollUpdate);
      this.scrollListenersAttached = true;
    }

    detachScrollListeners() {
      if (!this.scrollListenersAttached) {
        return;
      }

      window.removeEventListener('scroll', this.scheduleScrollUpdate);
      window.removeEventListener('resize', this.scheduleScrollUpdate);
      window.removeEventListener('load', this.scheduleScrollUpdate);
      this.scrollListenersAttached = false;
    }

    getStackSpeed() {
      const rawSpeed = this.sectionRoot ? parseFloat(this.sectionRoot.dataset.stackSpeed) : NaN;
      const speed = Number.isFinite(rawSpeed) ? rawSpeed / 100 : 1.3;

      return clamp(speed, 0.7, 1.8);
    }

    isScrollAnimationEnabled() {
      return !this.sectionRoot || this.sectionRoot.dataset.scrollAnimation !== 'false';
    }

    handleMediaChange() {
      this.refreshLayoutMetrics();

      if (this.isScrollAnimationEnabled() && desktopMedia.matches && !reducedMotionMedia.matches) {
        this.attachScrollListeners();
        this.scheduleScrollUpdate();
      } else {
        this.detachScrollListeners();
        this.updateCardsDiscrete(this.currentIndex);
      }
    }

    handleTriggerClick(event) {
      const trigger = event.currentTarget;
      const index = Number(trigger.dataset.tabIndex);

      this.activateIndex(index);
    }

    handleCardClick(event) {
      const card = event.currentTarget;
      const index = Number(card.dataset.tabIndex);

      this.activateIndex(index);
    }

    handleCardKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      event.preventDefault();
      const card = event.currentTarget;
      const index = Number(card.dataset.tabIndex);

      this.activateIndex(index);
    }

    activateIndex(index) {
      if (Number.isNaN(index)) {
        return;
      }

      this.setActive(index);
      this.scrollToIndex(index);
    }

    handleTriggerKeydown(event) {
      const currentIndex = Number(event.currentTarget.dataset.tabIndex);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = Math.min(this.triggers.length - 1, currentIndex + 1);
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = Math.max(0, currentIndex - 1);
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = this.triggers.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      this.triggers[nextIndex].focus();
      this.setActive(nextIndex);
      this.scrollToIndex(nextIndex);
    }

    scrollToIndex(index) {
      if (!this.isScrollAnimationEnabled() || !desktopMedia.matches || !this.steps[index]) {
        return;
      }

      this.steps[index].scrollIntoView({
        behavior: reducedMotionMedia.matches ? 'auto' : 'smooth',
        block: 'center'
      });
    }

    refreshLayoutMetrics() {
      const stackSpeed = this.getStackSpeed();

      this.sectionRoot.style.setProperty(
        '--scrollable-tabs-step-height',
        `${(108 / stackSpeed).toFixed(2)}vh`
      );

      if (this.panelContainer) {
        const maxHeight = this.panels.reduce((height, panel) => Math.max(height, panel.scrollHeight), 0);
        this.sectionRoot.style.setProperty('--scrollable-tabs-panel-height', `${Math.ceil(maxHeight)}px`);
      }

      if (desktopMedia.matches && this.triggerBar) {
        const headerHeight = this.stickyHeader ? this.stickyHeader.offsetHeight : this.triggerBar.offsetHeight;
        const bodyGap = this.body ? parseFloat(getComputedStyle(this.body).marginTop) || 0 : 0;

        this.sectionRoot.style.setProperty(
          '--scrollable-tabs-sticky-top',
          `${Math.ceil(headerHeight + bodyGap)}px`
        );
      } else {
        this.sectionRoot.style.setProperty('--scrollable-tabs-sticky-top', '12rem');
      }
    }

    getStackMetrics() {
      const width = window.innerWidth;
      const stickyTop =
        parseFloat(getComputedStyle(this.sectionRoot).getPropertyValue('--scrollable-tabs-sticky-top')) || 120;

      if (width <= 767) {
        return {
          activationTop: 120,
          futureOffsetX: 0,
          futureOffsetY: 24,
          futureRotate: 0,
          futureOpacity: 1,
          minOpacity: 1,
          futureScaleStep: 0.05,
          coverScaleStep: 0.02,
          scaleMin: 0.92,
          coverShiftX: 0,
          coverShiftY: 10,
          coverRotate: 0,
          coverFade: 0,
          stackStep: 10
        };
      }

      if (width <= 1280) {
        return {
          activationTop: stickyTop + 88,
          futureOffsetX: 0,
          futureOffsetY: 160,
          futureRotate: 0,
          futureOpacity: 1,
          minOpacity: 1,
          futureScaleStep: 0.07,
          coverScaleStep: 0.045,
          scaleMin: 0.85,
          coverShiftX: 0,
          coverShiftY: -30,
          coverRotate: 0,
          coverFade: 0,
          stackStep: 24
        };
      }

      return {
        activationTop: stickyTop + 108,
        futureOffsetX: 0,
        futureOffsetY: 220,
        futureRotate: 0,
        futureOpacity: 1,
        minOpacity: 1,
        futureScaleStep: 0.075,
        coverScaleStep: 0.05,
        scaleMin: 0.84,
        coverShiftX: 0,
        coverShiftY: -38,
        coverRotate: 0,
        coverFade: 0,
        stackStep: 28
      };
    }

    getStepProgress(rect, targetTop, range) {
      if (!rect) {
        return 0;
      }

      return clamp((targetTop + range - rect.top) / range, 0, 1);
    }

    getActiveIndex(stepRects, metrics) {
      let activeIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      stepRects.forEach((rect, index) => {
        const targetTop = metrics.activationTop + index * metrics.stackStep;
        const distance = Math.abs(rect.top - targetTop);

        if (distance < closestDistance) {
          closestDistance = distance;
          activeIndex = index;
        }
      });

      return activeIndex;
    }

    scheduleScrollUpdate() {
      if (this.ticking || !this.isScrollAnimationEnabled() || !desktopMedia.matches) {
        return;
      }

      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.updateFromScroll();
      });
    }

    updateFromScroll() {
      const metrics = this.getStackMetrics();
      const stepRects = this.steps.map((step) => step.getBoundingClientRect());
      const activeIndex = this.getActiveIndex(stepRects, metrics);

      if (activeIndex !== this.currentIndex) {
        this.setActive(activeIndex, { skipCardUpdate: true });
      }

      this.updateCardsContinuous(stepRects, metrics, activeIndex);
    }

    updateCardsContinuous(stepRects, metrics, activeIndex) {
      this.cards.forEach((card, cardIndex) => {
        const rect = stepRects[cardIndex];
        const baseRange = Math.max((rect ? rect.height : window.innerHeight) * 1.25, metrics.stackStep * 14, 320);
        const ownTargetTop = metrics.activationTop + Math.max(0, cardIndex - 1) * metrics.stackStep;
        const ownProgress = cardIndex === 0 ? 1 : this.getStepProgress(rect, ownTargetTop, baseRange);
        let coverProgress = 0;

        for (let nextIndex = cardIndex + 1; nextIndex < this.cards.length; nextIndex += 1) {
          const nextRect = stepRects[nextIndex];
          const range = Math.max(
            (nextRect ? nextRect.height : window.innerHeight) * 1.25,
            metrics.stackStep * 14,
            320
          );
          const targetTop = metrics.activationTop + (nextIndex - 1) * metrics.stackStep;
          coverProgress += this.getStepProgress(nextRect, targetTop, range);
        }

        const scale = Math.max(
          1 - coverProgress * metrics.coverScaleStep - (1 - ownProgress) * metrics.futureScaleStep,
          metrics.scaleMin
        );
        const translateX = (1 - ownProgress) * metrics.futureOffsetX + coverProgress * metrics.coverShiftX;
        const translateY = (1 - ownProgress) * metrics.futureOffsetY + coverProgress * metrics.coverShiftY;
        const rotate = (1 - ownProgress) * metrics.futureRotate - coverProgress * metrics.coverRotate;
        const opacity = 1;
        let zIndex = 20;

        if (cardIndex === activeIndex) {
          zIndex = 100;
        } else if (cardIndex < activeIndex) {
          zIndex = 80 - (activeIndex - cardIndex);
        } else {
          zIndex = 60 - (cardIndex - activeIndex);
        }

        card.style.setProperty('--card-translate-x', `${translateX.toFixed(2)}px`);
        card.style.setProperty('--card-translate', `${translateY.toFixed(2)}px`);
        card.style.setProperty('--card-scale', scale.toFixed(4));
        card.style.setProperty('--card-rotate', `${rotate.toFixed(2)}deg`);
        card.style.setProperty('--card-opacity', opacity.toFixed(4));
        card.style.setProperty('--card-z', `${zIndex}`);
      });
    }

    updateCardsDiscrete(activeIndex) {
      this.cards.forEach((card, cardIndex) => {
        const delta = cardIndex - activeIndex;
        const isActive = delta === 0;
        let translateX = 0;
        let translateY = 0;
        let scale = 1;
        let rotate = 0;
        let opacity = 1;
        let zIndex = 20;

        if (!desktopMedia.matches) {
          if (delta > 0) {
            translateY = 20;
            scale = 0.93;
            opacity = 0;
          } else if (delta < 0) {
            translateY = 10;
            scale = 0.92;
            opacity = 0;
          }
        } else if (delta > 0) {
          translateY = 160 * delta;
          scale = Math.max(1 - delta * 0.08, 0.88);
          zIndex = 60 - delta;
        } else if (delta < 0) {
          const depth = Math.abs(delta);
          translateX = 0;
          translateY = -34 * depth;
          scale = Math.max(1 - depth * 0.05, 0.84);
          zIndex = 80 - depth;
        } else {
          zIndex = 100;
        }

        card.style.setProperty('--card-translate-x', `${translateX.toFixed(2)}px`);
        card.style.setProperty('--card-translate', `${translateY.toFixed(2)}px`);
        card.style.setProperty('--card-scale', scale.toFixed(4));
        card.style.setProperty('--card-rotate', `${rotate.toFixed(2)}deg`);
        card.style.setProperty('--card-opacity', opacity.toFixed(4));
        card.style.setProperty('--card-z', `${zIndex}`);
        card.style.setProperty('--card-shadow-strength', '1');
        card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    setActive(index, options) {
      const config = options || {};

      if (index < 0 || index >= this.triggers.length) {
        return;
      }

      this.currentIndex = index;

      this.triggers.forEach((trigger, triggerIndex) => {
        const isActive = triggerIndex === index;
        trigger.classList.toggle('_active', isActive);
        trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
        trigger.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.panels.forEach((panel, panelIndex) => {
        const isActive = panelIndex === index;
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');

        if ('inert' in panel) {
          panel.inert = !isActive;
        } else if (isActive) {
          panel.removeAttribute('inert');
        } else {
          panel.setAttribute('inert', '');
        }
      });

      this.steps.forEach((step, stepIndex) => {
        step.classList.toggle('is-active', stepIndex === index);
      });

      this.cards.forEach((card, cardIndex) => {
        const isActive = cardIndex === index;
        card.classList.toggle('is-active', isActive);
        card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      if (!config.skipCardUpdate) {
        this.updateCardsDiscrete(index);
      }
    }

    destroy() {
      this.detachScrollListeners();

      this.triggers.forEach((trigger) => {
        trigger.removeEventListener('click', this.handleTriggerClick);
        trigger.removeEventListener('keydown', this.handleTriggerKeydown);
      });

      this.cards.forEach((card) => {
        card.removeEventListener('click', this.handleCardClick);
        card.removeEventListener('keydown', this.handleCardKeydown);
      });

      this.unbindMediaListener(desktopMedia, this.handleMediaChange);
      this.unbindMediaListener(reducedMotionMedia, this.handleMediaChange);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', this.refreshLayoutMetrics);
      }
    }
  }

  function mount(section) {
    if (!section || instances.has(section)) {
      return;
    }

    const instance = new ScrollableTabsSection(section);
    if (instance.root) {
      instances.set(section, instance);
    }
  }

  function unmount(section) {
    const instance = instances.get(section);
    if (!instance) {
      return;
    }

    instance.destroy();
    instances.delete(section);
  }

  function mountAll(scope) {
    const sections = (scope || document).querySelectorAll('[data-scrollable-tabs-section]');
    sections.forEach((node) => {
      const section = node.closest('.shopify-section') || node;
      mount(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      mountAll(document);
    });
  } else {
    mountAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    mountAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    const section = event.target.closest('.shopify-section') || event.target;
    unmount(section);
  });
})();
